import axios from "axios";

// Backend URL strategy:
//   * In preview / local dev → REACT_APP_BACKEND_URL is set to the
//     environment's external preview hostname so CRA dev-server can
//     reach the backend through Kubernetes ingress.
//   * In production (https://assospay.com) → REACT_APP_BACKEND_URL
//     MUST be either same-origin (`""` / empty) or `https://assospay.com`.
//     Any cross-origin value (e.g. an Emergent autogen hostname like
//     `thrill-exchange.emergent.host`) breaks the AssosPay session
//     cookie (HttpOnly+Secure cookies cannot be shared across origins
//     even with `withCredentials`), so `/api/auth/me` would 401 right
//     after a successful login or Assos365 link.
//
// Defense in depth: when the env var is missing or empty, fall back to
// the empty string so all `/api/...` calls go same-origin via the page.
// This keeps the bundle SAFE even if a future deployment forgets to
// set the env var.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 15000 });

// AssosPay session-aware client. The session cookie is HttpOnly + Secure,
// so the browser sends/stores it automatically — but the request must
// opt into credentials for cross-origin XHR.
export const sessionApi = axios.create({
  baseURL: API,
  timeout: 15000,
  withCredentials: true,
});

export async function getCoinInfo() {
  const { data } = await api.get("/coin/info");
  return data;
}
export async function getTokenomics() {
  const { data } = await api.get("/coin/tokenomics");
  return data;
}
export async function getRoadmap() {
  const { data } = await api.get("/coin/roadmap");
  return data;
}
export async function getTopMarkets() {
  const { data } = await api.get("/markets/top");
  return data;
}
export async function getPresaleStats() {
  const { data } = await api.get("/presale/stats");
  return data;
}
export async function postPresaleSignup(payload) {
  const { data } = await api.post("/presale/signup", payload);
  return data;
}
export async function getUtilityTiers() {
  const { data } = await api.get("/utility/tiers");
  return data;
}
export async function getUtilityContract() {
  const { data } = await api.get("/utility/contract");
  return data;
}
export async function getTournamentsPreview() {
  const { data } = await api.get("/tournaments/preview");
  return data;
}
export async function postWalletConnect(payload) {
  const { data } = await api.post("/wallet/connect", payload);
  return data;
}
export async function postAttestBalance(payload) {
  const { data } = await api.post("/integration/attest-balance", payload);
  return data;
}
export async function getFeeQuote(params) {
  const { data } = await api.get("/fees/quote", { params });
  return data;
}
export async function getShadowLog(limit = 200) {
  const { data } = await api.get("/fees/shadow-log", { params: { limit } });
  return data;
}


// ---------------- AP-4D — read-only redemption surface ----------------
// All four are GET-only. Frontend MUST NOT call POST /api/redemptions in
// the AP-4D shell.

export async function getArcRate() {
  const { data } = await api.get("/arc/rate");
  return data;
}

export async function getArcQuote(arcAmount) {
  const { data } = await api.get("/arc/quote", { params: { arc_amount: arcAmount } });
  return data;
}

export async function getAuthMe() {
  // Session-aware. Returns 401 when there's no session — callers handle
  // the error explicitly so the UI can render the "Link Assos365" CTA.
  const { data } = await sessionApi.get("/auth/me");
  return data;
}

export async function getMyRedemptions(limit = 20) {
  const { data } = await sessionApi.get("/redemptions", { params: { limit } });
  return data;
}

export async function getWalletMe() {
  // AP-5A.2 — read-only consolidated wallet/portfolio state for the
  // logged-in user. Session-aware. 401 propagates as an axios error
  // so callers can render the "Sign in with Assos365" state.
  const { data } = await sessionApi.get("/wallet/me");
  return data;
}

export async function postWalletMeSave({ walletAddress, chainId }) {
  // AP-5A.2.1 — session-bound save of the user's preferred external
  // wallet address. Strictly public fields only — the backend rejects
  // any extra field (`extra="forbid"` on the Pydantic body). No
  // signature, no transaction, no token transfer.
  const { data } = await sessionApi.post("/wallet/me", {
    wallet_address: walletAddress,
    chain_id: chainId ?? null,
  });
  return data;
}

export async function deleteWalletMe() {
  // AP-5A.2.2 — remove the user's saved wallet binding.
  // Idempotent: returns `{ ok:true, removed:false }` when no row
  // exists. Does not touch the legacy anonymous wallet_connections
  // collection. No signature, no tx.
  const { data } = await sessionApi.delete("/wallet/me");
  return data;
}

export async function postLinkCallback(linkToken) {
  // AP-4E only allowed POST. Cookie-aware: server's `Set-Cookie` for
  // `arc_session` is HttpOnly + Secure; the browser stores it as long
  // as `withCredentials: true` is set.
  const { data } = await sessionApi.post("/auth/assos365/callback", {
    link_token: linkToken,
  });
  emitAuthChanged({ kind: "assos365_link" });
  return data;
}

// ---------------- AP-5B.3 — session-change event bus ----------------
// Small custom-event channel so long-lived components (e.g. the Header)
// can re-probe `/api/auth/me` immediately after a sign-in / sign-out
// instead of waiting for a window focus event. The bus carries no token
// or secret — only a "something changed, re-fetch" signal.
const AUTH_EVENT = "arc:auth-changed";
function emitAuthChanged(detail) {
  try {
    window.dispatchEvent(new CustomEvent(AUTH_EVENT, { detail }));
  } catch (_) {
    // SSR / older browsers — silent best-effort.
  }
}
export const AUTH_CHANGED_EVENT = AUTH_EVENT;

export async function postLogout() {
  // AP-4E small follow-up. Idempotent — server returns 200 + clears
  // the cookie even when no session is present.
  const { data } = await sessionApi.post("/auth/logout");
  emitAuthChanged({ kind: "logout" });
  return data;
}

// ---------------- AP-5B.1 — Native AssosPay magic-link auth ----------------
// Strict client rules (mirror the backend contract):
//   * NEVER store the raw token in localStorage / sessionStorage.
//   * NEVER echo the raw token to console / analytics.
//   * Treat `debug_token` field as preview-only — exposed by the backend
//     only when `ASSOSPAY_NATIVE_DEBUG_TOKEN_EXPOSE=true`.

export async function postNativeRequest({ email, returnUrl } = {}) {
  // Always returns `{ ok: true, sent: true }` — and optionally
  // `debug_token` + `return_url` when the preview flag is on. We do NOT
  // throw on enumeration-safe responses; the only thrown axios error
  // is 429 rate_limited or a 5xx / network failure.
  const { data } = await sessionApi.post("/auth/native/request", {
    email,
    ...(returnUrl ? { return_url: returnUrl } : {}),
  });
  return data;
}

export async function postNativeConsume(token) {
  // Atomic single-use. On success the backend sets the same
  // `arc_session` cookie used by the Assos365 path. Errors propagate
  // as axios errors so the page can surface a typed message:
  //   400 native_invalid · 400 native_expired · 409 native_replayed.
  const { data } = await sessionApi.post("/auth/native/consume", {
    token,
  });
  emitAuthChanged({ kind: "native_consume" });
  return data;
}

// AP-4F — request-only redemption submit + status read.

export async function postRedemption({ arcAmount, metadata }) {
  // Decimal-safe: arcAmount MUST be a string. Do NOT parseFloat.
  // Server rejects floats explicitly. metadata is server-sanitised but
  // we still keep the surface tiny on purpose.
  const { data } = await sessionApi.post("/redemptions", {
    arc_amount: String(arcAmount),
    metadata: metadata || undefined,
  });
  return data;
}

export async function getRedemption(arcReferenceId) {
  // Same-user only on the server side. 401/403/404 propagate as
  // axios errors so the status page can map them.
  const { data } = await sessionApi.get(`/redemptions/${encodeURIComponent(arcReferenceId)}`);
  return data;
}
